import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

const GIFT_VARIANT_ID = 46602640130145;
const MINIMUM_SUBTOTAL = 249900;
const SOURCE = 'soniji-free-gift';

let syncPromise;
let syncAgain = false;
let retryingCheckout = false;

async function getCart() {
  const response = await fetch(`${Theme.routes.cart_url}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });

  if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
  return response.json();
}

function getSections() {
  return [...document.querySelectorAll('cart-items-component')]
    .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : null))
    .filter(Boolean);
}

async function updateGift(cart) {
  const giftItems = cart.items.filter((item) => item.variant_id === GIFT_VARIANT_ID);
  const subtotal = cart.items.reduce(
    (total, item) => total + (item.variant_id === GIFT_VARIANT_ID ? 0 : item.original_line_price),
    0
  );
  const eligible = subtotal >= MINIMUM_SUBTOTAL;
  const needsUpdate = giftItems.some((item, index) => item.quantity !== (eligible && index === 0 ? 1 : 0));

  if ((!eligible && giftItems.length === 0) || (eligible && giftItems.length === 1 && !needsUpdate)) return;

  const sections = getSections();
  const sectionParams = sections.length
    ? { sections: sections.join(','), sections_url: window.location.pathname }
    : {};
  const body =
    eligible && giftItems.length === 0
      ? {
          items: [{ id: GIFT_VARIANT_ID, quantity: 1, properties: { _free_gift: 'true' } }],
          ...sectionParams,
        }
      : {
          updates: Object.fromEntries(
            giftItems.map((item, index) => [item.key, eligible && index === 0 ? 1 : 0])
          ),
          ...sectionParams,
        };
  const deferred = CartLinesUpdateEvent.createPromise();

  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'update',
      context: 'cart',
      lines: [{ id: giftItems[0]?.key ?? String(GIFT_VARIANT_ID), quantity: eligible ? 1 : 0 }],
      promise: deferred.promise,
      detail: { source: SOURCE },
    })
  );

  try {
    const response = await fetch(
      eligible && giftItems.length === 0 ? Theme.routes.cart_add_url : `${Theme.routes.cart_update_url}.js`,
      fetchConfig('json', { body: JSON.stringify(body) })
    );
    const result = await response.json();

    if (!response.ok || result.errors) {
      throw new Error(result.description || result.message || result.errors || 'Failed to update free gift');
    }

    const updatedCart = await getCart();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(updatedCart),
      detail: {
        sections: result.sections,
        items: updatedCart.items,
        itemCount: updatedCart.item_count,
        source: SOURCE,
        didError: false,
      },
    });
  } catch (error) {
    deferred.reject(error);
    throw error;
  }
}

function syncGift() {
  if (syncPromise) {
    syncAgain = true;
    return syncPromise;
  }

  syncPromise = (async () => {
    do {
      syncAgain = false;
      await updateGift(await getCart());
    } while (syncAgain);
  })().finally(() => {
    syncPromise = undefined;
  });

  return syncPromise;
}

function queueSync() {
  void syncGift().catch((error) => console.error('[free-gift]', error));
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (event.detail?.source === SOURCE) return;
  event.promise?.then(queueSync).catch(() => {});
});

document.addEventListener(
  'click',
  (event) => {
    const button = event.target instanceof Element ? event.target.closest('.sr-headless-checkout') : null;
    if (!button || retryingCheckout) return;

    const productForm = button.closest('form[action*="/cart/add"]');

    event.preventDefault();
    event.stopImmediatePropagation();
    button.disabled = true;

    void (async () => {
      if (productForm) {
        const response = await fetch(Theme.routes.cart_add_url, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(productForm),
        });
        const result = await response.json();

        if (!response.ok || result.status) {
          throw new Error(result.description || result.message || 'Failed to add product to cart');
        }

        await syncGift();
        window.location.assign(Theme.routes.cart_url);
      } else {
        await syncGift();
        retryingCheckout = true;
        button.disabled = false;
        button.click();
        retryingCheckout = false;
      }
    })().catch((error) => {
      button.disabled = false;
      console.error('[free-gift]', error);
    });
  },
  true
);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', queueSync, { once: true });
} else {
  queueSync();
}

window.addEventListener('pageshow', queueSync);
